import { useEffect, useState } from "react";
import {
  Button,
  Empty,
  Form,
  Input,
  Modal,
  Tag,
  Upload,
  message,
  Image,
  Progress,
} from "antd";
import { PlusOutlined, UploadOutlined } from "@ant-design/icons";
import axios from "../../../requests/axios";
import routes from "../../../requests/routes";
import RichText from "../../admin/RichTextEditor/RichTextEditor";
import classes from "./MyBlog.module.scss";
import { normFile, rawFile, uploadMany, uploadOne } from "../../../utils/utils";

export const MyBlog = () => {
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);

  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewImage, setPreviewImage] = useState("");
  const [previewTitle, setPreviewTitle] = useState("");

  const [bannerPct, setBannerPct] = useState(0);
  const [galleryPct, setGalleryPct] = useState(0);
  const [galleryFilePct, setGalleryFilePct] = useState({}); // { uid: percent }

  const [form] = Form.useForm();

  const handlePreview = (file) => {
    setPreviewImage(file.url || file.thumbUrl);
    setPreviewOpen(true);
    setPreviewTitle(file.name || "");
  };

  const fetchMine = async () => {
    try {
      setLoading(true);
      const { data } = await axios.get(routes.getBlogs + "/me");
      setRows(data?.blogs || []);
    } catch {
      message.error("Failed to load blogs");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMine();
  }, []);

  const openCreate = () => {
    form.resetFields();
    setBannerPct(0);
    setGalleryPct(0);
    setGalleryFilePct({});
    setOpen(true);
  };

  const submit = async (values) => {
    // try {
    // reset UI progress
    setBannerPct(0);
    setGalleryPct(0);
    setGalleryFilePct({});

    // ---- COVER ----
    const coverList = values.coverImage || [];
    const coverFile = coverList[0] ? rawFile(coverList[0]) : null;

    let coverImage = values.coverImage || ""; // allow prefilled value (optional)
    if (coverFile) {
      coverImage = await uploadOne(coverFile, setBannerPct);
      setBannerPct(95);
    }

    // ---- GALLERY ----
    const galleryList = values.gallery || [];
    const galleryUrls = await uploadMany(
      galleryList,
      (uid, pct) => setGalleryFilePct((m) => ({ ...m, [uid]: pct })),
      (overall) => setGalleryPct(overall)
    );

    // ---- FINAL PAYLOAD (no file objects) ----
    const payload = {
      title: values.title,
      summary: values.summary || "",
      contentText: values.contentText, // your editor’s HTML
      coverImage, // string URL or ""
      gallery: galleryUrls, // flat array of strings
      tags: String(values.tags || "")
        .split(",")
        .map((t) => t.trim())
        .filter(Boolean),
    };

    await axios.post(routes.blogs, payload);
    message.success("Submitted for review");
    setOpen(false);
    fetchMine();
    // }
    //  catch (e) {
    //   message.error(e?.response?.data?.message || "Failed to submit");
    // }
  };

  return (
    <div className={classes.my_blog}>
      <div className={classes.my_blog__head}>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={openCreate}
          loading={loading}
        >
          Write a Blog
        </Button>
      </div>

      {rows.length === 0 ? (
        <Empty description="No blogs yet" />
      ) : (
        <div className={classes.my_blog__cards}>
          {rows.map((b) => (
            <div key={b._id} className={classes.my_blog__cards__card}>
              <Image
                height={200}
                style={{ objectFit: "cover" }}
                src={b.coverImage || b.image}
              />
              <p className={classes.my_blog__cards__card__title}>{b.title}</p>

              <div className={classes.my_blog__cards__card__summary}>
                <p className={classes.my_blog__cards__card__summary__title}>
                  Summary:
                </p>
                <p dangerouslySetInnerHTML={{ __html: b.summary || "" }} />
              </div>

              <div className={classes.my_blog__cards__card__tags}>
                <span>Tags:</span>
                {(b.tags || []).map((t, i) => (
                  <Tag key={i}>{t}</Tag>
                ))}
              </div>

              <div className={classes.my_blog__cards__card__footer}>
                <div className={classes.my_blog__cards__card__footer__status}>
                  Status:{" "}
                  {b.isPublished === true ? (
                    <Tag color="green">Approved</Tag>
                  ) : b.isPublished === false ? (
                    <Tag color="red">Declined</Tag>
                  ) : (
                    <Tag color="gold">Pending</Tag>
                  )}
                </div>

                <p className={classes.my_blog__cards__card__footer__date}>
                  <span>{new Date(b.created_at).toDateString()}</span>
                </p>
              </div>
            </div>
          ))}
        </div>
      )}

      <Modal
        open={open}
        title="Write a Blog"
        onCancel={() => setOpen(false)}
        onOk={() => form.submit()}
        width={900}
        okText="Submit"
        destroyOnClose
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={submit}
          initialValues={{
            title: "",
            summary: "",
            contentText: "",
            coverImage: [],
            gallery: [],
            tags: "",
          }}
        >
          <Form.Item
            label="Title"
            name="title"
            rules={[{ required: true, message: "Title is required" }]}
          >
            <Input placeholder="e.g. My Trip to Barcelona" />
          </Form.Item>

          <Form.Item label="Summary" name="summary">
            <Input.TextArea rows={3} placeholder="Short teaser" />
          </Form.Item>

          <Form.Item
            label="Content"
            name="contentText"
            rules={[
              { required: true, message: "Content is required" },
              {
                validator: (_, v) => {
                  const plain = String(v || "")
                    .replace(/<[^>]+>/g, "")
                    .trim();
                  return plain.length >= 20
                    ? Promise.resolve()
                    : Promise.reject("Minimum 20 characters");
                },
              },
            ]}
          >
            <RichText
              value={form.getFieldValue("contentText")}
              onChange={(html) => form.setFieldsValue({ contentText: html })}
            />
          </Form.Item>

          <Form.Item
            label="Banner"
            name="coverImage"
            valuePropName="fileList"
            getValueFromEvent={normFile}
          >
            <Upload
              listType="picture-card"
              maxCount={1}
              beforeUpload={() => false}
              onPreview={handlePreview}
            >
              <div>
                <UploadOutlined /> Upload
              </div>
            </Upload>
          </Form.Item>

          {bannerPct > 0 && <Progress percent={bannerPct} size="small" />}

          <Form.Item
            label="Gallery"
            name="gallery"
            valuePropName="fileList"
            getValueFromEvent={normFile}
          >
            <Upload
              listType="picture-card"
              multiple
              beforeUpload={() => false}
              onPreview={handlePreview}
            >
              <div>
                <UploadOutlined /> Upload
              </div>
            </Upload>
          </Form.Item>

          {/* per-image progress */}
          {(form.getFieldValue("gallery") || []).map((f) => (
            <div key={f.uid} style={{ marginBottom: 6 }}>
              <div style={{ fontSize: 12, opacity: 0.75 }}>{f.name}</div>
              <Progress percent={galleryFilePct[f.uid] || 0} size="small" />
            </div>
          ))}

          {/* overall gallery avg */}
          {(form.getFieldValue("gallery") || []).length > 1 && (
            <div style={{ marginTop: 8 }}>
              <div style={{ fontSize: 12, opacity: 0.75 }}>
                Gallery progress
              </div>
              <Progress percent={galleryPct} />
            </div>
          )}

          <Form.Item label="Tags (comma-separated)" name="tags">
            <Input placeholder="e.g. travel, barcelona, food" />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        open={previewOpen}
        title={previewTitle}
        footer={null}
        onCancel={() => setPreviewOpen(false)}
      >
        <img alt="preview" style={{ width: "100%" }} src={previewImage} />
      </Modal>
    </div>
  );
};

export default MyBlog;
